import { Component, inject, input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, ReplaySubject, timer } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
  timeout,
} from 'rxjs/operators';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { PaymentMethodHandles } from '@config/payment-methods.config';
import { Paths } from '@config/paths.config';
import { IVivaWalletTerminalPaymentIntent } from '@core/models/payment.model';
import { DoodOrderModel } from '@store/order/order.model';
import { DoodApiError } from '@shared/interfaces/error.interface';
import { logToJitsu } from '@shared/utils/logger/logger.utils';

import { BasketService } from '@core/services/basket/basket.service';
import { CartService } from '@core/services/cart/cart.service';
import { OrdersService } from '@core/services/orders/orders.service';
import { PaymentService } from '@core/services/payment/payment.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { TerminalAbortService } from '@core/services/yavin-terminal/terminal-abort.service';
import { VivaWalletTerminalService } from '@core/services/viva-wallet-terminal/viva-wallet-terminal.service';
import { PaymentStoreDispatcher } from '@common/dispatchers/payment.dispatcher';

@Component({
  selector: 'app-payment-panel-viva-wallet-terminal',
  templateUrl: './payment-panel-viva-wallet-terminal.component.html',
  standalone: false,
})
export class PaymentPanelVivaWalletTerminalComponent
  extends DestroyerBase
  implements OnInit, OnDestroy
{
  readonly terminalInstructionMessage = input<string>();

  isLoading = true;
  isOnError = false;
  errorMessage?: string;
  loadKioskResetTimer = false;

  private readonly router = inject(Router);
  private readonly cartService = inject(CartService);
  private readonly ordersService = inject(OrdersService);
  private readonly basketService = inject(BasketService);
  private readonly paymentService = inject(PaymentService);
  private readonly paymentDispatcher = inject(PaymentStoreDispatcher);
  private readonly routerHelper = inject(RouterHelperService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly terminalAbortService = inject(TerminalAbortService);
  private readonly vivaWalletTerminalService = inject(VivaWalletTerminalService);

  private orderId: string | null = null;
  private aborting = false;
  private saleReadyNotified = false;
  private saleReady$ = new ReplaySubject<string | null>(1);

  ngOnInit(): void {
    this.terminalAbortService.register(() => this.abortOnLeave$());

    timer(1)
      .pipe(takeUntil(this._destroyerRef))
      .subscribe(() => {
        this.paymentDispatcher.updateUI({
          error: null,
          button: { isEnabled: false, isVisible: false },
          status: { isLoading: false },
        });
        this.createOrderAndPay();
      });
  }

  ngOnDestroy(): void {
    this.terminalAbortService.unregister();
    super.ngOnDestroy();
  }

  createOrderAndPay(): void {
    if (!this.aborting) {
      this.orderId = null;
      this.saleReadyNotified = false;
      this.saleReady$ = new ReplaySubject<string | null>(1);
    }
    this.isOnError = false;
    this.isLoading = true;
    this.errorMessage = undefined;
    this.loadKioskResetTimer = false;

    this.ordersService
      .createOrder$(PaymentMethodHandles.VivaWalletTerminal)
      .pipe(
        takeUntil(this._destroyerRef),
        switchMap(order => {
          if (!order) {
            return of(null);
          }
          this.orderId = order.id;
          return this.paymentService
            .createOrderPaymentIntent$<IVivaWalletTerminalPaymentIntent>(order.id)
            .pipe(switchMap(() => of(order)));
        }),
        finalize(() => this.notifySaleReady()),
        tap(order => {
          this.isLoading = false;
          if (order) {
            this.listenToOrderStatusChange(order);
            return;
          }
          if (!this.aborting) {
            this.handleOrderError();
          }
        }),
      )
      .subscribe({
        error: (response: { error?: DoodApiError }) => {
          if (!this.aborting) {
            this.handleOrderError(response?.error);
          }
        },
      });
  }

  private abortOnLeave$(): Observable<boolean> {
    this.aborting = true;
    logToJitsu(
      this.analyticsService,
      'info',
      'viva_wallet_terminal_abort_requested',
      '[Viva Terminal] Abort requested by user',
      { orderId: this.orderId },
    );

    return this.saleReady$.pipe(
      take(1),
      timeout(20000),
      catchError(() => of(this.orderId)),
      switchMap(orderId => this.abortTransaction$(orderId)),
    );
  }

  private abortTransaction$(orderId: string | null): Observable<boolean> {
    if (!orderId) {
      return of(true);
    }

    return this.vivaWalletTerminalService.abortTransaction$(orderId).pipe(
      timeout(10000),
      map(response => {
        const success = response?.status === 'ok';
        logToJitsu(
          this.analyticsService,
          success ? 'info' : 'warn',
          'viva_wallet_terminal_abort_response',
          '[Viva Terminal] Abort response received',
          { orderId, status: response?.status },
        );
        return success;
      }),
      catchError(err => {
        logToJitsu(
          this.analyticsService,
          'error',
          'viva_wallet_terminal_abort_failed',
          '[Viva Terminal] Abort failed or timed out',
          { orderId, reason: err?.message ?? 'unknown' },
        );
        return of(false);
      }),
    );
  }

  private notifySaleReady(): void {
    if (this.saleReadyNotified) {
      return;
    }
    this.saleReadyNotified = true;
    this.saleReady$.next(this.orderId);
  }

  private listenToOrderStatusChange(order: DoodOrderModel): void {
    timer(0, 3000)
      .pipe(
        takeUntil(this._destroyerRef),
        switchMap(() => this.ordersService.getOrderById$(order.id)),
        tap((updatedOrder: DoodOrderModel) => {
          if (updatedOrder.status === 'CANCELLED') {
            this.handleOrderError();
            return;
          }
          if (updatedOrder.status !== 'PAYMENT') {
            this.handlePaymentSuccess(updatedOrder);
          }
        }),
        takeWhile((updatedOrder: DoodOrderModel) => updatedOrder.status === 'PAYMENT'),
      )
      .subscribe();
  }

  private handlePaymentSuccess(updatedOrder: DoodOrderModel): void {
    this.basketService.clearBasket();
    this.cartService.clearCart();
    this.terminalAbortService.unregister();
    this.router.navigate([
      this.routerHelper.translateRoute(`${Paths.Orders}/${updatedOrder.id}/${Paths.Status}`),
    ]);
  }

  private handleOrderError(error?: Partial<DoodApiError>): void {
    this.isLoading = false;
    this.isOnError = true;
    this.loadKioskResetTimer = true;
    this.errorMessage = error?.detail ?? 'Une erreur est survenue.';
  }

  backToCart(): void {
    const abort$ = this.terminalAbortService.abortIfNeeded$();
    if (!abort$) {
      this.navigateToCart();
      return;
    }
    abort$.subscribe(success => {
      if (success) {
        this.navigateToCart();
        return;
      }
      this.aborting = false;
      this.isOnError = true;
      this.isLoading = false;
      this.errorMessage = "L'annulation n'a pas pu être finalisée.";
    });
  }

  private navigateToCart(): void {
    this.terminalAbortService.unregister();
    this.router.navigate([this.routerHelper.translateRoute(`${Paths.Cart}`)]);
  }
}
